import { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Folders, Loader2, Star, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NetworkOption {
  id: string;
  name: string;
  is_default: boolean;
}

interface Props {
  brokerUserId: string;
  brokerLabel: string;
  networks: NetworkOption[];
  /** Called after a successful save so the parent can reload. */
  onSaved: () => void;
}

/**
 * Safe, non-destructive control for editing which groups a broker belongs to.
 *
 * Guardrail: if the save would leave the broker with ZERO accepted groups,
 * the save is BLOCKED with an inline hint pointing to "Remove from network".
 * This makes accidental full removal impossible through this control.
 */
export function BrokerManageGroupsPopover({
  brokerUserId,
  brokerLabel,
  networks,
  onSaved,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const ids = networks.map((n) => n.id);
      if (ids.length === 0) {
        if (!cancelled) { setInitial(new Set()); setChecked(new Set()); setLoading(false); }
        return;
      }
      const { data, error } = await supabase
        .from("shipper_network_brokers" as any)
        .select("network_id")
        .in("network_id", ids)
        .eq("broker_user_id", brokerUserId)
        .eq("status", "accepted");
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }
      const s = new Set<string>(((data as any[]) || []).map((r) => r.network_id));
      setInitial(s);
      setChecked(new Set(s));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, brokerUserId, networks]);

  const toggle = (id: string, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  };

  const { toAdd, toRemove, dirty, wouldZeroOut } = useMemo(() => {
    const toAdd: string[] = [];
    const toRemove: string[] = [];
    for (const id of checked) if (!initial.has(id)) toAdd.push(id);
    for (const id of initial) if (!checked.has(id)) toRemove.push(id);
    return {
      toAdd,
      toRemove,
      dirty: toAdd.length > 0 || toRemove.length > 0,
      wouldZeroOut: checked.size === 0,
    };
  }, [checked, initial]);

  const handleSave = async () => {
    if (!dirty) { setOpen(false); return; }
    if (wouldZeroOut) return; // guardrail; button is disabled but be defensive
    setSaving(true);

    // Removes: flip accepted rows for the unchecked groups → 'removed'.
    if (toRemove.length > 0) {
      const { error } = await supabase
        .from("shipper_network_brokers" as any)
        .update({ status: "removed", removed_at: new Date().toISOString() })
        .in("network_id", toRemove)
        .eq("broker_user_id", brokerUserId)
        .eq("status", "accepted");
      if (error) { setSaving(false); toast.error(error.message); return; }
    }

    // Adds: resurrect a prior removed row if present, else insert a fresh one.
    for (const netId of toAdd) {
      const { data: existing } = await supabase
        .from("shipper_network_brokers" as any)
        .select("id")
        .eq("network_id", netId)
        .eq("broker_user_id", brokerUserId)
        .neq("status", "accepted")
        .order("added_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const existingId = (existing as any)?.id as string | undefined;
      if (existingId) {
        const { error } = await supabase
          .from("shipper_network_brokers" as any)
          .update({ status: "accepted", removed_at: null, added_at: new Date().toISOString() })
          .eq("id", existingId);
        if (error) { setSaving(false); toast.error(error.message); return; }
      } else {
        const { error } = await supabase
          .from("shipper_network_brokers" as any)
          .insert({
            network_id: netId,
            broker_user_id: brokerUserId,
            status: "accepted",
          });
        if (error) { setSaving(false); toast.error(error.message); return; }
      }
    }

    setSaving(false);
    setOpen(false);
    toast.success(
      toAdd.length && toRemove.length
        ? "Group memberships updated"
        : toAdd.length
          ? `Added to ${toAdd.length} group${toAdd.length > 1 ? "s" : ""}`
          : `Removed from ${toRemove.length} group${toRemove.length > 1 ? "s" : ""}`
    );
    onSaved();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-muted-foreground hover:text-foreground"
          title="Manage groups"
        >
          <Folders className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
            Manage groups
          </div>
          <div className="text-xs text-foreground truncate mt-0.5">{brokerLabel}</div>
        </div>

        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <div className="p-4 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : networks.length === 0 ? (
            <div className="px-3 py-4 text-xs text-muted-foreground">No groups yet.</div>
          ) : (
            networks.map((n) => {
              const on = checked.has(n.id);
              return (
                <label
                  key={n.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40 cursor-pointer"
                >
                  <Checkbox
                    checked={on}
                    onCheckedChange={(v) => toggle(n.id, v === true)}
                  />
                  <span className="text-xs flex-1 truncate">{n.name}</span>
                  {n.is_default && (
                    <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[9px] uppercase font-bold gap-1">
                      <Star className="h-2.5 w-2.5" /> Default
                    </Badge>
                  )}
                </label>
              );
            })
          )}
        </div>

        {wouldZeroOut && (
          <div className="px-3 py-2 border-t border-border bg-destructive/5 flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
            <div className="text-[11px] leading-snug text-destructive">
              This would remove the broker from your network. Use{" "}
              <span className="font-semibold">Remove from network</span> instead.
            </div>
          </div>
        )}

        <div className="px-3 py-2 border-t border-border flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-7" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7 text-white"
            style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            disabled={!dirty || wouldZeroOut || saving}
            onClick={handleSave}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
